import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Wallet, Check } from "lucide-react";
import { Button, Card, PageHero, PhoneInput } from "../../../components";
import CustomerAddressField from "../../../components/CustomerAddressField/CustomerAddressField";
import ZmenaHeslaKarta from "./ZmenaHeslaKarta";
import EmailKarta from "./EmailKarta";
import {
  CUSTOMER_PROFILE_URL,
  CUSTOMER_ADDRESSES_URL,
  CUSTOMER_ADDRESS_URL,
} from "../../../features/UrlHelper";

/**
 * Profil ZAKAZNIKA (R40, R23).
 *
 * Obsluha ma na `/profil` svoju obrazovku (`ProfilObsluhy`) -- tato je ina
 * v tom, ze ukazuje saldo a spravuje dorucovacie adresy. Preto je to vlastna
 * sekcia a nie vetvenie vo vnutri tej povodnej.
 *
 * SALDO (R40): server posiela `dlh` aj `preplatok` ako KLADNE cisla, aby
 * zakaznik nemusel lustit znamienko (`users.actual_saldo` ma dlh zaporny).
 *
 * ADRESY: zoznam je ten isty komponent ako pri registracii, takze sa mapa ani
 * pravidla nezdvojuju. Rozdiel je len v tom, ze tu sa kazda zmena hned uklada
 * na server. Spresnenie pre vodica sa pise po znakoch -- to jedine sa uklada
 * az ked pisanie na chvilu ustane, inak by kazde pismeno bol jeden PATCH.
 */
const ONESKORENIE_POZNAMKY_MS = 800;
const MAX_ADRIES = 10;

/** Predvoľby, ktoré pozná `PhoneInput`. Od najdlhšej, aby `startsWith` sedel. */
const PREDVOLBY = ["+421", "+420", "+43", "+48", "+36"];

const eur = (v) => `${Number(v || 0).toFixed(2)} €`;

/** Co z adresy chodi na server. `id` tam nepatri, ide v ceste. */
const telo = (a) => ({
  address: a.address,
  address_note: a.address_note || "",
  latitude: a.latitude,
  longitude: a.longitude,
});

const rovnake = (a, b) =>
  a.address === b.address &&
  (a.address_note || "") === (b.address_note || "") &&
  String(a.latitude) === String(b.latitude) &&
  String(a.longitude) === String(b.longitude);

function ZakaznickyProfil() {
  const [profil, setProfil] = useState(null);
  const [adresy, setAdresy] = useState([]);
  const [nacitava, setNacitava] = useState(true);
  const [chybaAdries, setChybaAdries] = useState("");
  const [uklada, setUklada] = useState(false);

  /*
   * Telefón si mení zákazník sám.
   *
   * Meno zostáva na prevádzke — pod ním ho tam poznajú a pod ním má históriu.
   * Číslo je iný prípad: mení sa, zákazník o tom vie prvý a dokiaľ ho
   * neopraví, vodič mu pred dverami nezavolá.
   */
  const [telefon, setTelefon] = useState("");
  const [predvolba, setPredvolba] = useState("+421");
  const [telUklada, setTelUklada] = useState(false);
  const [telChyba, setTelChyba] = useState("");
  const [telHotovo, setTelHotovo] = useState(false);

  // Odlozene ulozenie spresnenia -- { id, praca }. V ref-e preto, aby ho
  // stihol odoslat aj odchod z obrazovky a rozpisana poznamka sa nestratila.
  const casovac = useRef(null);
  const odlozene = useRef(null);

  const nacitaj = useCallback(
    () =>
      axios.get(CUSTOMER_PROFILE_URL).then(({ data }) => {
        setProfil(data);
        setAdresy(data.addresses || []);
        return data;
      }),
    [],
  );

  useEffect(() => {
    nacitaj()
      .then((data) => rozlozTelefon(data?.phone_number))
      .catch(() => setChybaAdries("Údaje sa nepodarilo načítať."))
      .finally(() => setNacitava(false));
  }, [nacitaj]);

  /** Uložené číslo je v tvare `+421902…`; vstup ho chce po predvoľbe a zvyšku. */
  function rozlozTelefon(cislo) {
    const t = String(cislo || "");
    const predv = PREDVOLBY.find((x) => t.startsWith(x)) || "+421";
    setPredvolba(predv);
    setTelefon(t.startsWith(predv) ? t.slice(predv.length) : t.replace(/^\+/, ""));
  }

  async function ulozTelefon(e) {
    e.preventDefault();
    setTelUklada(true);
    setTelChyba("");
    setTelHotovo(false);
    try {
      const { data } = await axios.patch(CUSTOMER_PROFILE_URL, {
        phone_number: predvolba + telefon.replace(/\s/g, ""),
      });
      setProfil(data);
      rozlozTelefon(data.phone_number);
      setTelHotovo(true);
    } catch (err) {
      setTelChyba(err?.response?.data?.message || "Telefón sa nepodarilo uložiť.");
    } finally {
      setTelUklada(false);
    }
  }

  /** Odoslanie odlozeneho PATCH-u. Vola sa aj pri odchode z obrazovky. */
  const posliOdlozene = useCallback(() => {
    clearTimeout(casovac.current);
    const cakajuce = odlozene.current;
    odlozene.current = null;
    return cakajuce ? cakajuce.praca() : null;
  }, []);

  /**
   * Robi miesto pre novu zmenu. Dalsie pismeno v TEJ ISTEJ poznamke predosle
   * odlozene ulozenie nahradi (o to ide -- inak by kazde pismeno bol jeden
   * PATCH), ale zmena inej adresy sa este musi stihnut odoslat.
   */
  const uvolniOdlozene = useCallback(
    (preId) => {
      if (odlozene.current && odlozene.current.id === preId) clearTimeout(casovac.current);
      else posliOdlozene();
    },
    [posliOdlozene],
  );

  useEffect(() => () => { posliOdlozene(); }, [posliOdlozene]);

  /**
   * Zmena sa hned ukaze v UI, na server ide vzapati. Pri chybe sa stav vrati.
   *
   * `obnovit` (pridanie a odstranenie) zablokuje zoznam, kym server neodpovie
   * -- inak by dva rychle kliky na "Odstranit" poslali dva pozadavky. Ulozenie
   * spresnenia zoznam NEBLOKUJE, lebo clovek v tej chvili pise a pole by mu
   * pod rukami zosedivelo.
   */
  const vykonaj = useCallback(
    async (praca, { obnovit = false } = {}) => {
      if (obnovit) setUklada(true);
      setChybaAdries("");
      try {
        await praca();
        if (obnovit) await nacitaj();
      } catch (err) {
        setChybaAdries(err.response?.data?.message || "Zmenu sa nepodarilo uložiť.");
        // Nech obrazovka neukazuje nieco, co na serveri neplati.
        await nacitaj().catch(() => {});
      } finally {
        if (obnovit) setUklada(false);
      }
    },
    [nacitaj],
  );

  const zmenAdresy = (nove) => {
    const predtym = adresy;
    setAdresy(nove);

    if (nove.length > predtym.length) {
      posliOdlozene();
      const pridana = nove[nove.length - 1];
      vykonaj(() => axios.post(CUSTOMER_ADDRESSES_URL, telo(pridana)), { obnovit: true });
      return;
    }

    if (nove.length < predtym.length) {
      posliOdlozene();
      const odstranena = predtym.find((a) => !nove.some((n) => n.id && n.id === a.id));
      // Adresa bez `id` este nie je na serveri -- staci si vypytat pravdu.
      if (!odstranena?.id) {
        vykonaj(() => Promise.resolve(), { obnovit: true });
        return;
      }
      vykonaj(() => axios.delete(CUSTOMER_ADDRESS_URL(odstranena.id)), { obnovit: true });
      return;
    }

    const i = nove.findIndex((a, index) => !rovnake(a, predtym[index]));
    if (i < 0) return;

    const zmenena = nove[i];
    if (!zmenena.id) return; // este sa zaklada, PATCH by nemal co trafit

    const praca = () => vykonaj(() => axios.patch(CUSTOMER_ADDRESS_URL(zmenena.id), telo(zmenena)));
    uvolniOdlozene(zmenena.id);

    // Zmenila sa len poznamka? Potom sa caka, kym clovek dopise.
    if (zmenena.address === predtym[i].address) {
      odlozene.current = { id: zmenena.id, praca };
      casovac.current = setTimeout(posliOdlozene, ONESKORENIE_POZNAMKY_MS);
      return;
    }
    praca();
  };

  if (nacitava) {
    return (
      <div className="profile__section">
        <h2 className="profile__section-title">Môj profil</h2>
        <p>Načítavam...</p>
      </div>
    );
  }

  if (!profil) {
    return (
      <div className="profile__section">
        <h2 className="profile__section-title">Môj profil</h2>
        <p className="profile__form-error">{chybaAdries || "Údaje sa nepodarilo načítať."}</p>
      </div>
    );
  }

  const dlh = Number(profil.dlh || 0);
  const preplatok = Number(profil.preplatok || 0);

  return (
    <div className="profile__section profile__section--zakaznik">
      {/* To isté záhlavie ako objednávka a moje objednávky — zákazník chodí
          medzi nimi a každá obrazovka nemá vyzerať inak. */}
      <PageHero
        eyebrow="Nastavenia účtu"
        title="Môj"
        zvyraznene="profil"
        lead="Adresy, kontakt a stav vášho konta."
      />

      {/*
        Na desktope dva stĺpce po dvojiciach: konto | osobné údaje, potom
        adresy cez celú šírku, potom e-mail | heslo. Poradie je dôležité --
        keď široké adresy stáli medzi dvoma polovičnými kartami, každá
        z nich ostala v riadku sama a vedľa nej diera.
      */}
      <div className="profile__karty">
      {/* ── Saldo (R40) ── */}
      <Card>
        <Card.Header>
          <Card.Title>Vaše konto</Card.Title>
        </Card.Header>
        <Card.Body>
          <div
            className={[
              "profile__saldo",
              dlh > 0 ? "profile__saldo--dlh" : "",
              preplatok > 0 ? "profile__saldo--preplatok" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <Wallet size={22} aria-hidden="true" />
            {dlh > 0 && (
              <div>
                <strong className="profile__saldo-suma">{eur(dlh)}</strong>
                <span className="profile__saldo-popis">
                  Toľko máte u nás nezaplatené. Vyrovnať sa to dá pri najbližšom doručení.
                </span>
              </div>
            )}
            {preplatok > 0 && (
              <div>
                <strong className="profile__saldo-suma">{eur(preplatok)}</strong>
                <span className="profile__saldo-popis">
                  Toľko máte u nás na dobro. Odpočíta sa z ďalších objednávok.
                </span>
              </div>
            )}
            {dlh === 0 && preplatok === 0 && (
              <div>
                <strong className="profile__saldo-suma">Vyrovnané</strong>
                <span className="profile__saldo-popis">Nemáte žiadny nedoplatok.</span>
              </div>
            )}
          </div>
          <p className="profile__form-hint">
            Prehľad jednotlivých objednávok nájdete v sekcii{" "}
            <Link to="/moje-objednavky">Moje objednávky</Link>.
          </p>
        </Card.Body>
      </Card>

      {/* ── Osobné údaje ── */}
      <Card>
        <Card.Header>
          <Card.Title>Osobné údaje</Card.Title>
        </Card.Header>
        <Card.Body>
          <div className="profile__form">
            <div className="profile__form-row">
              <div className="profile__form-field">
                <label className="profile__form-label" htmlFor="zak-meno">Meno</label>
                <input id="zak-meno" className="profile__form-input" value={profil.name || ""} disabled />
              </div>
              <div className="profile__form-field">
                <label className="profile__form-label" htmlFor="zak-priezvisko">Priezvisko</label>
                <input id="zak-priezvisko" className="profile__form-input" value={profil.surname || ""} disabled />
              </div>
            </div>
            <form className="profile__form-field" onSubmit={ulozTelefon}>
              <label className="profile__form-label" htmlFor="zak-telefon">Telefón</label>
              <div className="profile__telefon">
                <PhoneInput
                  id="zak-telefon"
                  value={telefon}
                  prefix={predvolba}
                  onChange={(v) => { setTelefon(v); setTelHotovo(false); }}
                  onPrefixChange={(v) => { setPredvolba(v); setTelHotovo(false); }}
                  required
                />
                <Button type="submit" variant="outline" disabled={telUklada || !telefon.trim()}>
                  {telUklada ? "Ukladám…" : "Uložiť"}
                </Button>
              </div>
              {telChyba && <p className="profile__form-error" role="alert">{telChyba}</p>}
              {telHotovo && (
                <p className="profile__form-ok">
                  <Check size={14} aria-hidden="true" /> Telefón je uložený.
                </p>
              )}
              <p className="profile__form-hint">
                Vodič ho potrebuje, keby vás nenašiel. Meno mení prevádzka — ozvite sa
                im, ak nesedí.
              </p>
            </form>
          </div>
        </Card.Body>
      </Card>

      {/* ── Adresy (R23). Cez celú šírku, medzi dvoma dvojicami kariet. ── */}
      <Card className="profile__karta--siroka">
        <Card.Header>
          <Card.Title>Doručovacie adresy</Card.Title>
        </Card.Header>
        <Card.Body>
          {chybaAdries && (
            <p className="profile__form-error" role="alert">{chybaAdries}</p>
          )}
          <CustomerAddressField
            adresy={adresy}
            onChange={zmenAdresy}
            max={MAX_ADRIES}
            disabled={uklada}
          />
        </Card.Body>
      </Card>

      <EmailKarta
        email={profil.email || ""}
        onZmenene={(email) => setProfil((p) => ({ ...p, email }))}
      />

      <ZmenaHeslaKarta />
      </div>
    </div>
  );
}

export default ZakaznickyProfil;
